import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

// Custom component to detect text truncation
const TruncateTextFixedLength = ({
  text,
  onClickHandler = () => {},
  htmlElement = '',
  className = '',
  maxLength = 15,
}) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Handle null/undefined text
  const originalText = text || '-';

  // Initialize displayText immediately with originalText (no state needed)
  const displayText = originalText;

  const checkIfVisuallyTruncated = (element) => {
    if (!element) return false;

    // If htmlElement is provided, check all Typography children
    if (htmlElement) {
      const typographyElements = element.querySelectorAll('p, span, div');
      for (const el of typographyElements) {
        if (el.scrollWidth > el.clientWidth) {
          return true;
        }
      }
      return false;
    }

    // Otherwise check the element itself
    return element.scrollWidth > element.clientWidth;
  };

  const truncateText = () => {
    const element = textRef.current;

    if (!element) {
      setIsTruncated(false);
      return;
    }

    // Check if CSS truncation is actually happening synchronously
    const isVisuallyTruncated = checkIfVisuallyTruncated(element);
    setIsTruncated(isVisuallyTruncated);
  };

  const checkTruncation = () => {
    truncateText();
  };

  useEffect(() => {
    // Immediately check truncation on next frame when text changes
    requestAnimationFrame(() => {
      checkTruncation();
    });
  }, [originalText, maxLength]);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    // Use ResizeObserver to detect when element size changes
    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Tooltip title={isTruncated && originalText !== '-' ? originalText : ''} disableInteractive>
      <Box
        className="tempValueWrapper"
        onClick={onClickHandler}
        sx={{
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {htmlElement ? (
          <Box ref={textRef}>{htmlElement}</Box>
        ) : (
          <Typography
            ref={textRef}
            className={className}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {displayText}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default TruncateTextFixedLength;
